/**
 * Creator Studio Sidebar
 *
 * Implements the OTTO platform standard collapsible sidebar.
 *
 * CUSTOMIZATION ZONES (modified for CS):
 *   - NAV_SECTIONS array
 *   - Header branding (purple, Palette icon, "Creator Studio" / "Design Studio")
 *   - Section content (what renders inside each expanded section)
 *
 * FIXED ZONES (do NOT modify):
 *   - SectionHeader component
 *   - Collapsed icon layout
 *   - Footer (Settings, Home, Sign Out)
 *   - Click-outside overlay
 *   - CSS classes and transitions
 *
 * OTTO Platform Governance: sidebar-reference v1
 */

import { useEffect } from 'react';
import {
  Palette,
  LayoutDashboard,
  FolderOpen,
  Layers,
  Image,
  Download,
  Home,
  LogOut,
  X,
  ChevronDown,
  Stamp,
} from 'lucide-react';
import { usePanelStore, type SectionId } from '../stores/panelStore';
import { useAuthStore } from '../stores/authStore';
import { BrandPicker } from './editor/brand/BrandPicker';

const NAV_SECTIONS = [
  { id: 'studio',    icon: LayoutDashboard, label: 'Dashboard',    color: 'text-purple-400' },
  { id: 'projects',  icon: FolderOpen, label: 'Projects',       color: 'text-emerald-400' },
  { id: 'templates', icon: Layers,     label: 'Templates',      color: 'text-blue-400' },
  { id: 'brands',    icon: Stamp,      label: 'Brands',         color: 'text-pink-400' },
  { id: 'assets',    icon: Image,      label: 'Assets',         color: 'text-amber-400' },
  { id: 'exports',   icon: Download,   label: 'Exports',        color: 'text-cyan-400' },
] as const;

function SectionHeader({
  section,
  isOpen,
  isActive,
  onToggle,
  badge,
}: {
  section: typeof NAV_SECTIONS[number];
  isOpen: boolean;
  isActive: boolean;
  onToggle: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-3 w-full px-4 py-3 text-left transition-all duration-150 border-l-2 ${
        isActive
          ? 'bg-white/[0.04] border-cyan-400 shadow-[inset_4px_0_8px_-4px_rgba(6,182,212,0.2)]'
          : 'border-transparent hover:bg-white/[0.03]'
      }`}
    >
      <section.icon
        size={18}
        className={isActive ? section.color : 'text-zinc-500'}
      />
      <span className={`text-sm font-medium flex-1 ${isActive ? 'text-zinc-200' : 'text-zinc-400'}`}>
        {section.label}
      </span>
      {badge !== undefined && (
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-white/[0.06] text-zinc-500">
          {badge}
        </span>
      )}
      <ChevronDown
        size={14}
        className={`text-zinc-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

export default function Sidebar() {
  const {
    sidebarExpanded,
    activeSection,
    expandedSections,
    setActiveSection,
    toggleSection,
    collapseSidebar,
    expandSidebar,
  } = usePanelStore();

  const { logout } = useAuthStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        if (sidebarExpanded) {
          collapseSidebar();
        } else {
          expandSidebar();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarExpanded, collapseSidebar, expandSidebar]);

  const handleNavClick = (sectionId: SectionId) => {
    if (!sidebarExpanded) {
      expandSidebar(sectionId);
    } else {
      setActiveSection(sectionId);
      toggleSection(sectionId);
    }
  };

  const handleCollapse = () => {
    collapseSidebar();
  };

  const handleExpand = () => {
    expandSidebar();
  };

  return (
    <>
      {sidebarExpanded && (
        <div
          role="presentation"
          className="fixed inset-0 z-20 bg-transparent"
          onClick={handleCollapse}
        />
      )}

      <aside
        className={`
          relative h-full bg-zinc-950 border-r border-cyan-500/10
          shadow-[1px_0_15px_rgba(6,182,212,0.03)]
          transition-[width] duration-200 ease-out
          flex flex-col z-30 sidebar-glow-border
          ${sidebarExpanded ? 'w-56' : 'w-16'}
        `}
      >
        {sidebarExpanded ? (
          <div className="flex items-center px-4 py-4 gap-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0">
                <Palette size={18} className="text-purple-400" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold text-zinc-200 tracking-wide leading-tight">
                  Creator Studio
                </h1>
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 leading-tight">
                  Design Workspace
                </p>
              </div>
            </div>
            <button
              onClick={handleCollapse}
              className="p-1.5 rounded-lg hover:bg-white/[0.04] text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Collapse sidebar"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center p-3">
            <button
              onClick={() => handleExpand()}
              className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center hover:bg-purple-500/15 transition-all"
              title="Creator Studio"
            >
              <Palette size={18} className="text-purple-400" />
            </button>
          </div>
        )}

        {sidebarExpanded && <div className="mx-3 border-t border-white/[0.04] mb-2" />}

        <div className={`flex-1 min-h-0 overflow-y-auto sidebar-scroll ${sidebarExpanded ? '' : 'flex flex-col items-center gap-1.5 pt-3'}`}>
          {sidebarExpanded ? (
            <>
              {NAV_SECTIONS.map((section) => {
                const isActive = activeSection === section.id;
                const isOpen = expandedSections.has(section.id as SectionId);

                return (
                  <div key={section.id}>
                    <SectionHeader
                      section={section}
                      isOpen={isOpen}
                      isActive={isActive}
                      onToggle={() => handleNavClick(section.id as SectionId)}
                    />

                    {isOpen && (
                      <div className="animate-fade-in">
                        {section.id === 'studio' && (
                          <div className="px-4 py-2 space-y-1">
                            <p className="text-xs text-zinc-500 px-2 py-1">
                              Overview of your projects, templates, and recent exports at a glance.
                            </p>
                          </div>
                        )}
                        {section.id === 'projects' && (
                          <div className="px-4 py-2 space-y-1">
                            <p className="text-xs text-zinc-500 px-2 py-1">
                              Manage your design projects and saved compositions.
                            </p>
                          </div>
                        )}
                        {section.id === 'templates' && (
                          <div className="px-4 py-2 space-y-1">
                            <p className="text-xs text-zinc-500 px-2 py-1">
                              Browse and manage reusable design templates.
                            </p>
                          </div>
                        )}
                        {section.id === 'brands' && (
                          <div className="h-64">
                            <BrandPicker />
                          </div>
                        )}
                        {section.id === 'assets' && (
                          <div className="px-4 py-2 space-y-1">
                            <p className="text-xs text-zinc-500 px-2 py-1">
                              Shared asset library — logos, images, brand elements.
                            </p>
                          </div>
                        )}
                        {section.id === 'exports' && (
                          <div className="px-4 py-2 space-y-1">
                            <p className="text-xs text-zinc-500 px-2 py-1">
                              View and manage exported asset packets (JPEG + .js overlay files).
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {NAV_SECTIONS.map((section) => {
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleNavClick(section.id as SectionId)}
                    className={`
                      relative flex items-center justify-center w-12 h-12 rounded-xl
                      transition-all duration-200
                      ${isActive
                        ? `${section.color} bg-white/[0.08] ring-2 ring-cyan-500/30 shadow-[0_0_12px_rgba(6,182,212,0.12)]`
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                      }
                    `}
                    title={section.label}
                    aria-label={section.label}
                  >
                    <section.icon size={24} />
                  </button>
                );
              })}
            </>
          )}
        </div>

        {sidebarExpanded ? (
          <div className="mt-auto border-t border-white/[0.04] px-3 py-3">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <a
                  href="https://home.abcotto.com"
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-colors text-xs"
                  title="OTTO Home"
                >
                  <Home size={16} />
                  <span>Home</span>
                </a>
                <button
                  onClick={() => logout()}
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-colors text-xs"
                  title="Sign Out"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-auto border-t border-white/[0.04] py-3 flex flex-col items-center gap-1.5">
            <a
              href="https://home.abcotto.com"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] transition-colors"
              title="OTTO Home"
            >
              <Home size={18} />
            </a>
            <button
              onClick={() => logout()}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] transition-colors"
              title="Sign Out"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
